// Facilitator/admin review + purge of suspicious open-link responses (handoff
// Section 18). Shown only for open-link cycles. Open-link responses have no
// identity; the signals are answer sparsity and a repeated device fingerprint.

import { useCallback, useEffect, useState, type JSX } from 'react';
import { healthApi, type OpenLinkResponseRow } from './api';
import { roleLabel, formatNzDateShort } from './format';

export function OpenLinkPurgeCard({
  cycleId,
  reload,
}: {
  cycleId: string;
  reload: () => void;
}): JSX.Element {
  const [rows, setRows] = useState<OpenLinkResponseRow[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purging, setPurging] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    healthApi
      .openLinkResponses(cycleId)
      .then((res) => {
        setRows(res.responses);
        // Pre-select the ones flagged suspicious so a purge is one click.
        setSelected(new Set(res.responses.filter((r) => r.suspicious).map((r) => r.response_id)));
      })
      .catch(() => setError('The open-link responses could not be loaded. Please try again.'))
      .finally(() => setLoading(false));
  }, [cycleId]);

  useEffect(() => load(), [load]);

  const toggle = (id: string): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const purge = (): void => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (
      !window.confirm(
        `Permanently remove ${ids.length} open-link ${ids.length === 1 ? 'response' : 'responses'}? ` +
          'This cannot be undone. Rostered responses are never affected.',
      )
    ) {
      return;
    }
    setPurging(true);
    setNotice(null);
    healthApi
      .purgeOpenLink(cycleId, ids)
      .then((res) => {
        setNotice(
          `${res.purged} open-link ${res.purged === 1 ? 'response' : 'responses'} removed.`,
        );
        load();
        reload();
      })
      .catch(() => setNotice('The responses could not be removed. Please try again.'))
      .finally(() => setPurging(false));
  };

  const suspiciousCount = (rows ?? []).filter((r) => r.suspicious).length;

  return (
    <div className="card" style={{ borderLeft: '4px solid var(--band-priority)' }}>
      <h3 style={{ fontSize: 15, marginBottom: 6 }}>Open-link responses</h3>
      <p style={{ fontSize: 13, color: 'var(--health-grey)', lineHeight: 1.7, marginBottom: 12 }}>
        Anyone with the shared link can submit, so open-link cycles can attract junk. Review the
        self-served responses below and remove any that look like abuse — barely answered, or many
        from the same device. Rostered (invited) responses never appear here and can never be
        removed from this panel.
      </p>

      {notice && (
        <div
          className="health-banner health-banner-blue"
          role="status"
          style={{ marginBottom: 12 }}
        >
          {notice}
        </div>
      )}
      {error && (
        <p style={{ fontSize: 13, color: 'var(--band-priority)', marginBottom: 12 }}>{error}</p>
      )}

      {loading ? (
        <p style={{ fontSize: 13, color: 'var(--health-grey)' }}>Loading...</p>
      ) : (rows ?? []).length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--health-grey)' }}>No open-link responses yet.</p>
      ) : (
        <>
          <p style={{ fontSize: 13, marginBottom: 10 }}>
            {rows?.length} open-link {rows?.length === 1 ? 'response' : 'responses'};{' '}
            <strong>{suspiciousCount}</strong> flagged as suspicious (pre-selected).
          </p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col" style={{ width: 34 }} aria-label="Select" />
                  <th scope="col">Submitted</th>
                  <th scope="col">Role</th>
                  <th scope="col">Answers</th>
                  <th scope="col">Device</th>
                  <th scope="col">Why flagged</th>
                </tr>
              </thead>
              <tbody>
                {(rows ?? []).map((r) => (
                  <tr
                    key={r.response_id}
                    style={
                      r.suspicious
                        ? { background: 'var(--band-priority-soft, #fbeeee)' }
                        : undefined
                    }
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(r.response_id)}
                        onChange={() => toggle(r.response_id)}
                        aria-label={`Select response submitted ${formatNzDateShort(r.submitted_at)}`}
                      />
                    </td>
                    <td>{formatNzDateShort(r.submitted_at)}</td>
                    <td>
                      {roleLabel(r.role) || <span style={{ color: 'var(--health-grey)' }}>—</span>}
                    </td>
                    <td>{r.answer_count}</td>
                    <td>
                      {r.ua_fingerprint || '—'}
                      {r.ua_duplicate_count > 1 ? ` (×${r.ua_duplicate_count})` : ''}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {[r.sparse ? 'barely answered' : null, r.duplicate ? 'repeated device' : null]
                        .filter(Boolean)
                        .join(', ') || <span style={{ color: 'var(--health-grey)' }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              disabled={purging || selected.size === 0}
              onClick={purge}
            >
              {purging ? 'Removing...' : `Purge selected (${selected.size})`}
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={purging}
              onClick={() =>
                setSelected(
                  new Set((rows ?? []).filter((r) => r.suspicious).map((r) => r.response_id)),
                )
              }
            >
              Select flagged
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={purging || selected.size === 0}
              onClick={() => setSelected(new Set())}
            >
              Clear selection
            </button>
          </div>
        </>
      )}
    </div>
  );
}
